import { Fragment, type JSX, type ReactNode } from 'react';

/**
 * Estate-wide viewer tiers for the shared Settings surface. Ordered
 * least → most privileged. Every portal maps its own native role onto one of
 * these before handing it to <SettingsDrawer>; @cca/ui never resolves the tier
 * itself (it can't — it never fetches). See the CCAANZ portal-admin standard.
 *
 *   user       any signed-in person — their own account & preferences
 *   admin      a portal's elevated non-registrar role (today only Finance)
 *   superuser  the registrar (uniform across the estate: email === REGISTRAR_EMAIL)
 */
export type ViewerTier = 'user' | 'admin' | 'superuser';

// Keyed by the full union → indexing is always a number (clean even under
// noUncheckedIndexedAccess, and total, so no fallback needed).
const TIER_RANK: Record<ViewerTier, number> = { user: 0, admin: 1, superuser: 2 };

/** True when `viewer` is at or above `required`. */
export function roleAtLeast(viewer: ViewerTier, required: ViewerTier): boolean {
  return TIER_RANK[viewer] >= TIER_RANK[required];
}

/**
 * The one place the whole estate decides "what tier is this viewer" — so five
 * portals derive it identically instead of five ad-hoc mappings. Pass whatever
 * the portal already knows:
 *   - `isRegistrar` (the uniform superuser rule), OR `email` + `registrarEmail`
 *     to derive it (case-insensitive);
 *   - `isAdmin` or a native `role === 'admin'` for the middle tier.
 */
export interface ViewerTierInput {
  isRegistrar?: boolean;
  isAdmin?: boolean;
  role?: string | null;
  email?: string | null;
  registrarEmail?: string | null;
}

export function resolveViewerTier(input: ViewerTierInput): ViewerTier {
  const registrar =
    input.isRegistrar ??
    (!!input.email &&
      !!input.registrarEmail &&
      input.email.trim().toLowerCase() === input.registrarEmail.trim().toLowerCase());
  if (registrar) return 'superuser';
  if (input.isAdmin === true || input.role === 'admin') return 'admin';
  return 'user';
}

/**
 * Declarative gate for an individual CONTROL inside a section body — renders its
 * children only when the viewer meets `min`. (Section-level gating is done by the
 * drawer via each section's `minRole`; this is for finer, in-body gating.)
 */
export function RoleGate({
  viewer,
  min,
  children,
}: {
  viewer: ViewerTier;
  min: ViewerTier;
  children: ReactNode;
}): JSX.Element | null {
  return roleAtLeast(viewer, min) ? <Fragment>{children}</Fragment> : null;
}
